import { frappeMethod } from "@/lib/erp/utils";
import type { DocFieldSchema } from "@/lib/erp/meta";
import type { ChildTableRow, RowValidationError } from "../types";

/**
 * Validate a Link field value
 * 
 * Checks:
 * 1. Document exists
 * 2. Document is not cancelled
 */
export async function validateLinkField(
  doctype: string,
  value: string | null | undefined
): Promise<{ valid: boolean; cancelled?: boolean; error?: string }> {
  if (!value) {
    return { valid: false, error: "Value is required" };
  }
  
  try {
    // Use Frappe's client.get method to fetch the document
    const doc = await frappeMethod<Record<string, any>>("frappe.client.get", {
      doctype,
      name: value,
    });
    
    if (!doc) {
      return { valid: false, error: "Document not found" };
    }
    
    // Check if document is cancelled
    if (doc.docstatus === 2) {
      return { valid: false, cancelled: true, error: "Cannot link cancelled document" };
    }
    
    return { valid: true };
  } catch (error) {
    return { 
      valid: false, 
      error: error instanceof Error ? error.message : "Validation failed" 
    };
  }
}

/**
 * Validate all Link fields in a row
 */
export async function validateRowLinks(
  row: ChildTableRow,
  rowIndex: number,
  fields: DocFieldSchema[]
): Promise<RowValidationError[]> {
  const errors: RowValidationError[] = [];
  
  // Skip validation for deleted rows
  if (row.__deleted) {
    return errors;
  }
  
  // Find all Link fields
  const linkFields = fields.filter((f) => f.fieldtype === "Link" && f.options);
  
  // Validate each Link field
  for (const field of linkFields) {
    const value = row[field.fieldname];
    
    if (value) {
      const validation = await validateLinkField(field.options!, value);
      
      if (!validation.valid) {
        errors.push({
          rowIndex,
          fieldname: field.fieldname,
          fieldLabel: field.label,
          message: `${field.label}: ${validation.error || "Invalid link"} (row ${rowIndex + 1})`,
        });
      }
    }
  }
  
  return errors;
}

